import * as React from 'react';
import { useEffect, useState } from 'react';
import { generateUrl } from '../../../Utilities/api';
function scoreToBand(score) {
    if (score < 40)
        return 'red';
    if (score < 70)
        return 'yellow';
    return 'green';
}
// ── Constants ──────────────────────────────────────────────────────────────
const BRAND = {
    blue: '#00537e',
    blueDeep: '#004360',
    tint: '#EEF4F7',
    red: '#f93f43',
    yellow: '#efe300',
    green: '#138f38',
};
const BAND_COLORS = {
    red: BRAND.red,
    yellow: BRAND.yellow,
    green: BRAND.green,
};
const BAND_LABELS = {
    red: 'Handlungsbedarf',
    yellow: 'Entwicklungspotenzial',
    green: 'Gut',
};
const CATEGORY_LABELS = {
    kraft: 'Kraft',
    beweglichkeit: 'Beweglichkeit',
    gleichgewicht: 'Gleichgewicht',
    koerperzusammensetzung: 'Körperzusammensetzung',
    schmerzfreiheit: 'Schmerzfreiheit',
    stressfreiheit: 'Stressfreiheit',
    energie: 'Energie',
    ernaehrung: 'Ernährung',
    mental: 'Mental',
    environment: 'Umfeld',
    regeneration: 'Regeneration',
    training: 'Training',
};
const CATEGORY_ORDER = [
    'kraft', 'beweglichkeit', 'gleichgewicht', 'koerperzusammensetzung',
    'schmerzfreiheit', 'stressfreiheit', 'energie',
    'ernaehrung', 'mental', 'environment', 'regeneration', 'training',
];
const IST_ZUSTAND = ['kraft', 'beweglichkeit', 'gleichgewicht', 'koerperzusammensetzung', 'schmerzfreiheit', 'stressfreiheit', 'energie'];
const VERHALTEN = ['ernaehrung', 'mental', 'environment', 'regeneration', 'training'];
function formatDate(iso) {
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
// ── Score Ring ─────────────────────────────────────────────────────────────
function ScoreRing({ score, size = 96, strokeWidth = 8 }) {
    const band = scoreToBand(score);
    const color = BAND_COLORS[band];
    const r = (size - strokeWidth) / 2;
    const circ = 2 * Math.PI * r;
    const dash = (score / 100) * circ;
    return (<svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth}/>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth} strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round"/>
            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" fontSize={size * 0.24} fontWeight="700" fill="#111827" style={{ transform: 'rotate(90deg)', transformOrigin: '50% 50%' }}>
                {score}
            </text>
        </svg>);
}
// ── Category Card ──────────────────────────────────────────────────────────
function CategoryCard({ category, entry }) {
    const color = BAND_COLORS[entry.band];
    return (<div className="bg-white rounded-2xl p-4 flex flex-col gap-3" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)' }}>
            <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700">{CATEGORY_LABELS[category] ?? category}</span>
                <div className="flex items-center gap-1.5">
                    <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: color }}/>
                    <span className="text-xs text-gray-400">{BAND_LABELS[entry.band]}</span>
                </div>
            </div>
            <div className="flex items-center gap-3">
                <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${entry.score}%`, backgroundColor: color }}/>
                </div>
                <span className="text-sm font-bold shrink-0" style={{ color: BRAND.blue }}>{entry.score}</span>
            </div>
            {entry.interpretation !== null && (<p className="text-xs text-gray-600 leading-relaxed border-t border-gray-100 pt-2">
                    {entry.interpretation}
                </p>)}
        </div>);
}
// ── Skeleton ───────────────────────────────────────────────────────────────
function Skeleton() {
    return (<div className="flex flex-col gap-6 animate-pulse">
            <div className="bg-white rounded-xl shadow-sm p-6 flex gap-6">
                <div className="w-24 h-24 rounded-full bg-gray-100"/>
                <div className="flex-1 flex flex-col gap-2 justify-center">
                    <div className="h-4 w-32 bg-gray-100 rounded"/>
                    <div className="h-6 w-20 bg-gray-100 rounded"/>
                    <div className="h-3 w-40 bg-gray-100 rounded"/>
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: 12 }).map((_, i) => (<div key={i} className="bg-white rounded-xl shadow-sm p-4 h-24 bg-gray-100"/>))}
            </div>
        </div>);
}
const HealthscoreDetail = ({ token, historyUrl, testUrl }) => {
    const [record, setRecord] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    useEffect(() => {
        void fetch(generateUrl('sitzkrieger_v2_myareaapi_healthscore_v2_record', { token }), { credentials: 'same-origin' })
            .then(r => {
            if (!r.ok)
                throw new Error();
            return r.json();
        })
            .then(setRecord)
            .catch(() => setIsError(true))
            .finally(() => setIsLoading(false));
    }, [token]);
    if (isLoading)
        return <Skeleton />;
    if (isError || record === null) {
        return (<div className="flex flex-col items-center justify-center py-16 gap-4">
                <p className="text-gray-500">Dieser Test konnte nicht geladen werden.</p>
                <a href={historyUrl} className="text-sm font-semibold" style={{ color: BRAND.blue }}>
                    Zum Verlauf
                </a>
            </div>);
    }
    const band = scoreToBand(record.score);
    const orderedCategories = CATEGORY_ORDER.filter(k => record.groupScores[k] !== undefined);
    const istZustandCategories = orderedCategories.filter(k => IST_ZUSTAND.includes(k));
    const verhaltensCategories = orderedCategories.filter(k => VERHALTEN.includes(k));
    return (<div className="flex flex-col gap-6">

            {/* Header card */}
            <div className="bg-white rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)' }}>
                <ScoreRing score={record.score} size={96} strokeWidth={8}/>
                <div className="flex-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Gesamt-Score</p>
                    <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-bold" style={{ color: BAND_COLORS[band] }}>{record.score}</span>
                        <span className="text-lg font-medium text-gray-400">/ 100</span>
                    </div>
                    <p className="text-sm text-gray-400 mt-1">{BAND_LABELS[band]}</p>
                    <p className="text-xs text-gray-400 mt-2">{formatDate(record.finishedAt)}</p>
                </div>
                <div className="flex flex-col gap-2 sm:items-end">
                    <a href={testUrl} className="text-sm font-semibold px-4 py-2 rounded-lg text-white whitespace-nowrap" style={{ backgroundColor: BRAND.blue }}>
                        Neuen Test starten
                    </a>
                    <a href={historyUrl} className="text-sm font-medium whitespace-nowrap" style={{ color: BRAND.blue }}>
                        Alle Tests ansehen
                    </a>
                </div>
            </div>

            {/* IST-Zustand section */}
            {istZustandCategories.length > 0 && (<div>
                    <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">IST-Zustand</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {istZustandCategories.map(k => (<CategoryCard key={k} category={k} entry={record.groupScores[k]}/>))}
                    </div>
                </div>)}

            {/* Verhalten section */}
            {verhaltensCategories.length > 0 && (<div>
                    <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Verhalten</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {verhaltensCategories.map(k => (<CategoryCard key={k} category={k} entry={record.groupScores[k]}/>))}
                    </div>
                </div>)}

        </div>);
};
HealthscoreDetail.displayName = 'HealthscoreDetail';
export default HealthscoreDetail;
